import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { examsService } from '@/services/exams.service';
import { Save, Plus, Trash2, AlertCircle, CheckCircle } from 'lucide-react';
import type { BlueprintEntry } from '@/types';

export function BlueprintEditor() {
  const { id: examId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [entries, setEntries] = useState<BlueprintEntry[]>([]);
  const [hasChanges, setHasChanges] = useState(false);

  const { data: exam } = useQuery({
    queryKey: ['exam', examId],
    queryFn: async () => {
      try {
        return await examsService.getExamById(examId!);
      } catch (error) {
        console.error('Failed to fetch exam:', error);
        return null;
      }
    },
    enabled: !!examId,
  });

  const { data: topics } = useQuery({
    queryKey: ['topics', examId],
    queryFn: async () => {
      try {
        return await (examsService as any).getExamTopics(examId!);
      } catch (error) {
        console.error('Failed to fetch topics:', error);
        return [];
      }
    },
    enabled: !!examId,
  });

  const { data: blueprint, isLoading } = useQuery({
    queryKey: ['blueprint', examId],
    queryFn: async () => {
      try {
        return await (examsService as any).getBlueprint(examId!);
      } catch (error) {
        console.error('Failed to fetch blueprint:', error);
        return [];
      }
    },
    enabled: !!examId,
  });

  // Initialize entries when blueprint or topics data loads
  useEffect(() => {
    if (blueprint && blueprint.length > 0) {
      setEntries(blueprint);
    } else if (topics && topics.length > 0 && entries.length === 0) {
      // Initialize with default entries from topics
      const defaultEntries: BlueprintEntry[] = topics.map((topic: any) => ({
        blueprint_id: '',
        exam_id: examId!,
        topic_id: topic.topic_id,
        weight_percent: Math.floor(100 / topics.length),
        difficulty_distribution: {
          easy: 30,
          medium: 50,
          hard: 20,
        },
        question_types_allowed: ['MCQ'],
        min_questions: 1,
        max_questions: 10,
      } as any));
      setEntries(defaultEntries);
    }
  }, [blueprint, topics, examId, entries.length]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      return await (examsService as any).updateBlueprint(examId!, entries);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blueprint', examId] });
      setHasChanges(false);
    },
  });

  const handleWeightChange = (index: number, value: number) => {
    const newEntries = [...entries];
    newEntries[index].weight_percent = value;
    setEntries(newEntries);
    setHasChanges(true);
  };

  const handleDifficultyChange = (index: number, difficulty: 'easy' | 'medium' | 'hard', value: number) => {
    const newEntries = [...entries];
    newEntries[index].difficulty_distribution[difficulty] = value;
    setEntries(newEntries);
    setHasChanges(true);
  };

  const handleMinQuestionsChange = (index: number, value: number) => {
    const newEntries = [...entries];
    (newEntries[index] as any).min_questions = value;
    setEntries(newEntries);
    setHasChanges(true);
  };

  const handleMaxQuestionsChange = (index: number, value: number) => {
    const newEntries = [...entries];
    (newEntries[index] as any).max_questions = value;
    setEntries(newEntries);
    setHasChanges(true);
  };

  const addEntry = () => {
    if (topics && topics.length > 0) {
      const newEntry: any = {
        blueprint_id: '',
        exam_id: examId!,
        topic_id: topics[0].topic_id,
        weight_percent: 10,
        difficulty_distribution: {
          easy: 30,
          medium: 50,
          hard: 20,
        },
        question_types_allowed: ['MCQ'],
        min_questions: 1,
        max_questions: 10,
      };
      setEntries([...entries, newEntry]);
      setHasChanges(true);
    }
  };

  const removeEntry = (index: number) => {
    const newEntries = entries.filter((_, i) => i !== index);
    setEntries(newEntries);
    setHasChanges(true);
  };

  const handleSave = () => {
    saveMutation.mutate();
  };

  const totalWeight = entries.reduce((sum, entry) => sum + entry.weight_percent, 0);
  const isWeightValid = totalWeight === 100;

  const getTopic = (topicId: string) => {
    return topics?.find((t: any) => t.topic_id === topicId);
  };

  const getDifficultyTotal = (entry: BlueprintEntry) => {
    return entry.difficulty_distribution.easy +
           entry.difficulty_distribution.medium +
           entry.difficulty_distribution.hard;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-gray-500">Loading blueprint...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Blueprint Editor</h1>
          <p className="text-gray-600 mt-1">
            {exam?.name || `Exam ${examId}`}
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button onClick={() => navigate('/exams')} className="btn-secondary">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!hasChanges || !isWeightValid || saveMutation.isPending}
            className="btn-primary flex items-center"
          >
            <Save className="w-4 h-4 mr-2" />
            {saveMutation.isPending ? 'Saving...' : 'Save Blueprint'}
          </button>
        </div>
      </div>

      {/* Weight Validation Alert */}
      <div className={`card ${isWeightValid ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
        <div className="flex items-start space-x-3">
          {isWeightValid ? (
            <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
          ) : (
            <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
          )}
          <div className="flex-1">
            <p className={`text-sm font-medium ${isWeightValid ? 'text-green-900' : 'text-red-900'}`}>
              Total Weight: {totalWeight}%
            </p>
            <p className={`text-xs ${isWeightValid ? 'text-green-700' : 'text-red-700'}`}>
              {isWeightValid
                ? 'Blueprint weights are balanced correctly'
                : 'Blueprint weights must total exactly 100%'}
            </p>
          </div>
        </div>
      </div>

      {/* Blueprint Entries */}
      <div className="space-y-4">
        {entries.map((entry, index) => {
          const topic = getTopic(entry.topic_id);
          const diffTotal = getDifficultyTotal(entry);
          const isDiffValid = diffTotal === 100;

          return (
            <div key={index} className="card space-y-4">
              {/* Entry Header */}
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <select
                      value={entry.topic_id}
                      onChange={(e) => {
                        const newEntries = [...entries];
                        newEntries[index].topic_id = e.target.value;
                        setEntries(newEntries);
                        setHasChanges(true);
                      }}
                      className="input-field font-semibold text-gray-900"
                    >
                      {topics?.map((topic: any) => (
                        <option key={topic.topic_id} value={topic.topic_id}>
                          {topic.topic_code}: {topic.topic_name}
                        </option>
                      ))}
                    </select>
                  </div>
                  {topic && (
                    <p className="text-sm text-gray-600">{topic.description}</p>
                  )}
                </div>

                <button
                  onClick={() => removeEntry(index)}
                  className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded transition-colors ml-4"
                  title="Remove Entry"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              {/* Weight Configuration */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Weight (%)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={entry.weight_percent}
                    onChange={(e) => handleWeightChange(index, parseInt(e.target.value) || 0)}
                    className="input-field"
                  />
                  <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary-600 transition-all"
                      style={{ width: `${entry.weight_percent}%` }}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Min Questions
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={(entry as any).min_questions}
                    onChange={(e) => handleMinQuestionsChange(index, parseInt(e.target.value) || 0)}
                    className="input-field"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Max Questions
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={(entry as any).max_questions}
                    onChange={(e) => handleMaxQuestionsChange(index, parseInt(e.target.value) || 0)}
                    className="input-field"
                  />
                </div>
              </div>

              {/* Difficulty Distribution */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Difficulty Distribution
                  </label>
                  <span className={`text-sm ${isDiffValid ? 'text-green-600' : 'text-red-600'}`}>
                    Total: {diffTotal}%
                  </span>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Easy (%)</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={entry.difficulty_distribution.easy}
                      onChange={(e) =>
                        handleDifficultyChange(index, 'easy', parseInt(e.target.value) || 0)
                      }
                      className="input-field text-sm"
                    />
                    <div className="mt-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-green-500 transition-all"
                        style={{ width: `${entry.difficulty_distribution.easy}%` }}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Medium (%)</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={entry.difficulty_distribution.medium}
                      onChange={(e) =>
                        handleDifficultyChange(index, 'medium', parseInt(e.target.value) || 0)
                      }
                      className="input-field text-sm"
                    />
                    <div className="mt-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-yellow-500 transition-all"
                        style={{ width: `${entry.difficulty_distribution.medium}%` }}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Hard (%)</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={entry.difficulty_distribution.hard}
                      onChange={(e) =>
                        handleDifficultyChange(index, 'hard', parseInt(e.target.value) || 0)
                      }
                      className="input-field text-sm"
                    />
                    <div className="mt-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-red-500 transition-all"
                        style={{ width: `${entry.difficulty_distribution.hard}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Add Entry Button */}
      <button onClick={addEntry} className="btn-secondary w-full flex items-center justify-center">
        <Plus className="w-4 h-4 mr-2" />
        Add Topic Entry
      </button>

      {/* Summary */}
      <div className="card bg-gray-50">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Blueprint Summary</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-sm text-gray-600">Total Topics</p>
            <p className="text-2xl font-bold text-gray-900">{entries.length}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Total Weight</p>
            <p className={`text-2xl font-bold ${isWeightValid ? 'text-green-600' : 'text-red-600'}`}>
              {totalWeight}%
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Min Questions</p>
            <p className="text-2xl font-bold text-gray-900">
              {entries.reduce((sum, e) => sum + (e as any).min_questions, 0)}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Max Questions</p>
            <p className="text-2xl font-bold text-gray-900">
              {entries.reduce((sum, e) => sum + (e as any).max_questions, 0)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
